/* ═══════════════════════════════════════════════
   NEBS SHOPIFY THEME — theme.js
   New England Build Supply Corp.
═══════════════════════════════════════════════ */

'use strict';

/* ── CART API ── */
const Cart = {
  async getCart() {
    const res = await fetch('/cart.js');
    return res.json();
  },
  async addItem(variantId, quantity = 1, properties = {}) {
    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ id: variantId, quantity, properties })
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.description || 'Failed to add item');
    }
    return res.json();
  },
  async updateItem(key, quantity) {
    const res = await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ id: key, quantity })
    });
    return res.json();
  },
  async removeItem(key) {
    return this.updateItem(key, 0);
  }
};

/* ── CART DRAWER ── */
const CartDrawer = {
  drawer: null,
  body: null,
  subtotalEl: null,

  init() {
    this.drawer = document.getElementById('cart-drawer');
    this.body   = document.getElementById('cart-drawer-body');
    this.subtotalEl = document.getElementById('cart-subtotal');
    if (!this.drawer) return;

    // Open
    document.querySelectorAll('[data-cart-open], .header__cart-btn').forEach(btn => {
      btn.addEventListener('click', () => this.open());
    });
    // Close
    document.querySelectorAll('[data-cart-close], .cart-drawer__overlay').forEach(el => {
      el.addEventListener('click', () => this.close());
    });
    // Escape
    document.addEventListener('keydown', e => {
      if (e.key === 'Escape' && this.drawer.getAttribute('aria-hidden') === 'false') this.close();
    });
    // Delegated events inside drawer
    this.drawer.addEventListener('click', e => {
      const removeBtn = e.target.closest('.cart-item__remove');
      if (removeBtn) { e.preventDefault(); this.handleRemove(removeBtn.dataset.key); return; }
      const qtyBtn = e.target.closest('.qty-btn');
      if (qtyBtn) { this.handleQtyChange(qtyBtn.dataset.key, parseInt(qtyBtn.dataset.qty)); }
    });
  },

  open() {
    this.drawer.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
    this.refresh();
  },

  close() {
    this.drawer.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
  },

  async refresh() {
    try {
      const cart = await Cart.getCart();
      this.render(cart);
    } catch (err) { console.error('Cart refresh error:', err); }
  },

  render(cart) {
    const countEl = document.getElementById('cart-drawer-count');
    if (countEl) countEl.textContent = cart.item_count;
    if (this.subtotalEl) this.subtotalEl.textContent = formatMoney(cart.total_price);
    this.updateHeaderCount(cart.item_count);
    if (!this.body) return;

    if (cart.item_count === 0) {
      this.body.innerHTML = `
        <div class="cart-drawer__empty">
          <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
            <path d="M8 8h4l5.5 20h17l5.5-16H14" stroke="#9AA3AE" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            <circle cx="22" cy="38" r="2.5" fill="#9AA3AE"/>
            <circle cx="34" cy="38" r="2.5" fill="#9AA3AE"/>
          </svg>
          <p>Your cart is empty</p>
          <a href="/collections/all" class="btn-primary" data-cart-close>Browse Products</a>
        </div>`;
      return;
    }

    const itemsHtml = cart.items.map(item => `
      <div class="cart-item" data-item-key="${item.key}">
        <div class="cart-item__image">
          ${item.image
            ? `<img src="${item.image.replace(/\?.*/, '')}?width=120&height=120&crop=center" alt="${escapeHtml(item.title)}" width="64" height="64" loading="lazy">`
            : '<div class="cart-item__image-placeholder"></div>'}
        </div>
        <div class="cart-item__info">
          <div class="cart-item__name">${escapeHtml(item.product_title)}</div>
          ${item.variant_title && item.variant_title !== 'Default Title'
            ? `<div class="cart-item__variant">${escapeHtml(item.variant_title)}</div>` : ''}
          <div class="cart-item__price">${formatMoney(item.final_line_price)}</div>
          <div class="cart-item__qty">
            <button class="qty-btn" data-key="${item.key}" data-qty="${Math.max(0, item.quantity - 1)}" aria-label="Decrease quantity">−</button>
            <span class="qty-val">${item.quantity}</span>
            <button class="qty-btn" data-key="${item.key}" data-qty="${item.quantity + 1}" aria-label="Increase quantity">+</button>
          </div>
        </div>
        <button class="cart-item__remove" data-key="${item.key}" aria-label="Remove ${escapeHtml(item.title)}">
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
            <path d="M2 2l10 10M12 2L2 12" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
          </svg>
        </button>
      </div>`).join('');

    this.body.innerHTML = `<div id="cart-items-list">${itemsHtml}</div>`;
    // Sync subtotal in footer
    const footerSubtotal = this.drawer.querySelector('#cart-subtotal');
    if (footerSubtotal) footerSubtotal.textContent = formatMoney(cart.total_price);
  },

  async handleRemove(key) {
    try { const cart = await Cart.removeItem(key); this.render(cart); }
    catch (err) { console.error(err); }
  },

  async handleQtyChange(key, qty) {
    if (qty < 0) return;
    try { const cart = await Cart.updateItem(key, qty); this.render(cart); }
    catch (err) { console.error(err); }
  },

  updateHeaderCount(count) {
    document.querySelectorAll('.header__cart-count').forEach(el => {
      el.textContent = count;
      el.style.display = count > 0 ? 'flex' : 'none';
    });
    document.body.dataset.cartCount = count;
  }
};

/* ── ADD TO CART ── */
const AddToCart = {
  init() {
    // Quick-add buttons (not inside a product form)
    document.addEventListener('click', async e => {
      const btn = e.target.closest('[data-add-to-cart]');
      if (!btn) return;
      // If inside a product form, let the form submit handler deal with it
      if (btn.type === 'submit' && btn.closest('[data-product-form]')) return;
      e.preventDefault();

      const variantId = btn.dataset.variantId;
      if (!variantId) return;

      const originalText = btn.textContent;
      btn.textContent = 'Adding…';
      btn.disabled = true;

      try {
        await Cart.addItem(parseInt(variantId));
        btn.textContent = '✓ Added';
        setTimeout(() => { btn.textContent = originalText; btn.disabled = false; }, 1500);
        CartDrawer.open();
      } catch (err) {
        btn.textContent = 'Error — try again';
        btn.disabled = false;
        console.error(err);
      }
    });

    // Full product form submit
    document.addEventListener('submit', async e => {
      const form = e.target.closest('[data-product-form]');
      if (!form) return;
      e.preventDefault();

      const variantInput = form.querySelector('[name="id"]');
      const qtyInput = form.querySelector('[name="quantity"]');
      if (!variantInput) return;

      const variantId = parseInt(variantInput.value);
      const quantity  = parseInt(qtyInput?.value || 1);
      const submitBtn = form.querySelector('[type="submit"]');
      const originalText = submitBtn?.textContent;
      if (submitBtn) { submitBtn.textContent = 'Adding…'; submitBtn.disabled = true; }

      try {
        await Cart.addItem(variantId, quantity);
        if (submitBtn) submitBtn.textContent = '✓ Added to cart!';
        setTimeout(() => {
          if (submitBtn) { submitBtn.textContent = originalText; submitBtn.disabled = false; }
        }, 2000);
        CartDrawer.open();
      } catch (err) {
        if (submitBtn) { submitBtn.textContent = 'Failed — try again'; submitBtn.disabled = false; }
        console.error(err);
      }
    });
  }
};

/* ── MOBILE MENU ── */
const MobileMenu = {
  menu: null,
  init() {
    this.menu = document.getElementById('mobile-menu');
    if (!this.menu) return;

    document.querySelectorAll('[data-menu-open], .header__menu-btn').forEach(btn => {
      btn.addEventListener('click', () => { this.open(); btn.setAttribute('aria-expanded', 'true'); });
    });
    document.querySelectorAll('[data-menu-close], .mobile-menu__overlay').forEach(el => {
      el.addEventListener('click', () => this.close());
    });
    document.addEventListener('keydown', e => {
      if (e.key === 'Escape' && this.menu.getAttribute('aria-hidden') === 'false') this.close();
    });
  },
  open()  { this.menu.setAttribute('aria-hidden', 'false'); document.body.style.overflow = 'hidden'; },
  close() { this.menu.setAttribute('aria-hidden', 'true');  document.body.style.overflow = '';
    document.querySelectorAll('[data-menu-open]').forEach(b => b.setAttribute('aria-expanded', 'false'));
  }
};

/* ── VARIANT SELECTOR ── */
const VariantSelector = {
  init() {
    document.querySelectorAll('[data-variant-selector]').forEach(selector => {
      const form = selector.closest('[data-product-form]');
      if (!form) return;
      selector.addEventListener('change', () => this.updateVariant(form));
    });

    // Swatch / button-style variant clicks
    document.addEventListener('click', e => {
      const btn = e.target.closest('.variant-btn');
      if (!btn) return;
      const group = btn.closest('.variant-options');
      if (!group) return;
      group.querySelectorAll('.variant-btn').forEach(b => b.classList.remove('active'));
      btn.classList.add('active');
      const select = group.querySelector('[data-option-selector]');
      if (select) {
        select.value = btn.dataset.value;
        select.dispatchEvent(new Event('change', { bubbles: true }));
      }
    });
  },

  updateVariant(form) {
    const selectedOptions = Array.from(form.querySelectorAll('[data-option-selector]')).map(el => el.value);
    const variantDataEl   = form.querySelector('[data-product-variants]');
    if (!variantDataEl) return;

    let variants;
    try { variants = JSON.parse(variantDataEl.textContent); } catch { return; }

    const matched = variants.find(v => v.options.every((opt, i) => opt === selectedOptions[i]));
    if (!matched) return;

    const idInput = form.querySelector('[name="id"]');
    if (idInput) idInput.value = matched.id;

    // Current price (update only the span, not the whole div — preserves compare-at sibling)
    const productPage = form.closest('.product-page');
    const currentPriceEl = productPage?.querySelector('[data-product-current-price]');
    if (currentPriceEl) currentPriceEl.textContent = formatMoney(matched.price);

    // Compare price
    const comparePriceEl = productPage?.querySelector('.product-page__compare-price');
    if (comparePriceEl) {
      if (matched.compare_at_price > matched.price) {
        comparePriceEl.textContent = formatMoney(matched.compare_at_price);
        comparePriceEl.style.display = '';
      } else {
        comparePriceEl.style.display = 'none';
      }
    }

    // Add to cart button
    const addBtn = form.querySelector('[type="submit"]');
    if (addBtn) {
      addBtn.disabled = !matched.available;
      addBtn.textContent = matched.available ? 'Add to Cart' : 'Sold Out';
    }

    // Stock indicator
    const stockEl = form.closest('.product-page')?.querySelector('[data-stock-indicator]');
    if (stockEl) {
      if (!matched.available) {
        stockEl.className = 'stock-indicator stock-indicator--out';
        stockEl.textContent = 'Out of Stock';
      } else if (matched.inventory_quantity > 0 && matched.inventory_quantity <= 10) {
        stockEl.className = 'stock-indicator stock-indicator--low';
        stockEl.textContent = `Low Stock — ${matched.inventory_quantity} left`;
      } else {
        stockEl.className = 'stock-indicator stock-indicator--in';
        stockEl.textContent = 'In Stock';
      }
    }

    // Update URL without reload
    const url = new URL(window.location);
    url.searchParams.set('variant', matched.id);
    window.history.replaceState({}, '', url);
  }
};

/* ── PRODUCT GALLERY ── */
const ProductGallery = {
  init() {
    document.querySelectorAll('.product-page__thumb').forEach(thumb => {
      thumb.addEventListener('click', () => {
        const main = document.querySelector('.product-page__main-image img');
        if (!main) return;

        // data-full-src is on the <button> element — set directly in Liquid, always reliable
        // Fallback: resize the thumbnail img src from 150 → 900px via URL param
        if (thumb.dataset.fullSrc) {
          main.src = thumb.dataset.fullSrc;
        } else {
          const img = thumb.querySelector('img');
          if (img) main.src = img.src.replace(/([?&]width=)\d+/g, '$1900');
        }

        document.querySelectorAll('.product-page__thumb').forEach(t => t.classList.remove('active'));
        thumb.classList.add('active');
      });
    });
  }
};

/* ── QUANTITY CONTROLS (product page) ── */
const QtyControls = {
  init() {
    document.addEventListener('click', e => {
      const btn = e.target.closest('[data-qty-change]');
      if (!btn) return;
      const wrap  = btn.closest('.qty-control');
      if (!wrap) return;
      const input = wrap.querySelector('input[name="quantity"]');
      if (!input) return;
      const current = parseInt(input.value) || 1;
      if (btn.dataset.qtyChange === 'increase') input.value = current + 1;
      if (btn.dataset.qtyChange === 'decrease' && current > 1) input.value = current - 1;
    });
  }
};

/* ── FILTER BUTTONS ── */
const FilterButtons = {
  init() {
    document.addEventListener('click', e => {
      const btn = e.target.closest('.filter-btn');
      if (!btn) return;
      const group = btn.closest('[data-filter-group]') || btn.closest('.product-filters');
      if (!group) return;
      group.querySelectorAll('.filter-btn').forEach(b => b.classList.remove('active'));
      btn.classList.add('active');

      const filter = btn.dataset.filter;
      if (!filter) return;

      const grid = document.querySelector('[data-filterable-grid]');
      if (!grid) return;

      let visibleCount = 0;
      grid.querySelectorAll('[data-filter-tag]').forEach(card => {
        const tags   = (card.dataset.filterTag || '').split(',').map(t => t.trim().toLowerCase());
        const show   = filter === 'all' || tags.includes(filter.toLowerCase());
        card.style.display = show ? '' : 'none';
        if (show) visibleCount++;
      });
    });
  }
};

/* ── STICKY HEADER ── */
const StickyHeader = {
  init() {
    const header = document.querySelector('.site-header');
    if (!header) return;
    let lastScroll = 0;
    window.addEventListener('scroll', () => {
      const current = window.scrollY;
      header.classList.toggle('is-scrolled', current > 10);
      lastScroll = current;
    }, { passive: true });
  }
};

/* ── NEWSLETTER FORM ── */
const Newsletter = {
  init() {
    document.querySelectorAll('[data-newsletter-form]').forEach(form => {
      form.addEventListener('submit', e => {
        // Let Shopify handle the POST — just show loading state
        const btn = form.querySelector('button[type="submit"]');
        if (btn) { btn.textContent = 'Subscribing…'; btn.disabled = true; }
        // If Shopify returns to same page with ?customer_posted=true, we handle that server-side via Liquid
      });
    });
  }
};

/* ── QUOTE FORM ── */
const QuoteForm = {
  init() {
    document.querySelectorAll('[data-quote-form]').forEach(form => {
      form.addEventListener('submit', () => {
        // Let Shopify native contact form handle submission — just show loading state
        const btn = form.querySelector('[type="submit"]');
        if (btn) { btn.textContent = 'Submitting…'; btn.disabled = true; }
      });
    });
  }
};

/* ── CART PAGE ── */
const CartPage = {
  init() {
    // AJAX qty update on cart page
    document.addEventListener('change', async e => {
      const input = e.target.closest('.cart-qty-control input[type="number"]');
      if (!input) return;
      // Walk up to the <tr> which has data-cart-line-key
      const row = input.closest('tr[data-cart-line-key]');
      if (!row) return;
      const key = row.dataset.cartLineKey;
      const qty = parseInt(input.value);
      if (isNaN(qty) || qty < 0) return;
      try {
        const cart = await Cart.updateItem(key, qty);
        CartDrawer.updateHeaderCount(cart.item_count);
        const item = cart.items.find(i => i.key === key);
        const priceEl = row.querySelector('[data-line-price]');
        if (priceEl && item) priceEl.textContent = formatMoney(item.final_line_price);
        const totalEl = document.querySelector('[data-cart-total]');
        if (totalEl) totalEl.textContent = formatMoney(cart.total_price);
        const subtotalSpan = document.querySelector('.cart-summary__row [data-cart-subtotal]');
        if (subtotalSpan) subtotalSpan.textContent = formatMoney(cart.total_price);
        if (qty === 0) row.remove();
      } catch (err) { console.error(err); }
    });

    // AJAX remove on cart page
    document.addEventListener('click', async e => {
      const removeLink = e.target.closest('[data-ajax-remove]');
      if (!removeLink) return;
      e.preventDefault();
      const key = removeLink.dataset.ajaxRemove;
      if (!key) return;
      try {
        const cart = await Cart.removeItem(key);
        CartDrawer.updateHeaderCount(cart.item_count);
        const row = removeLink.closest('tr');
        if (row) row.remove();
        const totalEl = document.querySelector('[data-cart-total]');
        if (totalEl) totalEl.textContent = formatMoney(cart.total_price);
        if (cart.item_count === 0) window.location.reload();
      } catch (err) { console.error(err); }
    });
  }
};

/* ── ANNOUNCEMENT BAR CLOSE ── */
const AnnouncementBar = {
  init() {
    const bar      = document.querySelector('.announcement-bar');
    const closeBtn = bar?.querySelector('[data-announce-close]');
    if (!closeBtn) return;
    closeBtn.addEventListener('click', () => {
      bar.style.transition = 'max-height 0.3s ease, padding 0.3s ease, opacity 0.3s ease';
      bar.style.maxHeight  = bar.offsetHeight + 'px';
      bar.style.overflow   = 'hidden';
      requestAnimationFrame(() => {
        bar.style.maxHeight = '0';
        bar.style.padding   = '0';
        bar.style.opacity   = '0';
      });
    });
  }
};

/* ── SCROLL REVEAL ── */
const ScrollReveal = {
  init() {
    if (!('IntersectionObserver' in window)) return;
    const els = document.querySelectorAll('[data-reveal]');
    if (!els.length) return;
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) { entry.target.classList.add('revealed'); observer.unobserve(entry.target); }
      });
    }, { threshold: 0.08, rootMargin: '0px 0px -30px 0px' });
    els.forEach(el => observer.observe(el));
  }
};

/* ── SEARCH ── */
const Search = {
  init() {
    const input = document.querySelector('.header__search-input');
    if (!input) return;
    // Prevent empty search
    input.closest('form')?.addEventListener('submit', e => {
      if (!input.value.trim()) e.preventDefault();
    });
    // Expand/collapse search on mobile
    const searchBtn = document.querySelector('.header__search-btn');
    const form      = document.querySelector('.header__search-form');
    if (searchBtn && form) {
      searchBtn.addEventListener('click', e => {
        if (window.innerWidth <= 768) {
          e.preventDefault();
          form.classList.toggle('is-open');
          if (form.classList.contains('is-open')) input.focus();
        }
      });
    }
  }
};

/* ── HELPERS ── */
function formatMoney(cents) {
  if (window.moneyFormat) {
    return window.moneyFormat.replace('{{amount}}', (cents / 100).toFixed(2))
      .replace('{{amount_no_decimals}}', Math.floor(cents / 100))
      .replace('{{amount_with_comma_separator}}', (cents / 100).toFixed(2).replace('.', ','));
  }
  return '$' + (cents / 100).toFixed(2).replace(/\d(?=(\d{3})+\.)/g, '$&,');
}

function escapeHtml(str) {
  return String(str).replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;').replace(/"/g,'&quot;');
}

/* ── INIT ── */
document.addEventListener('DOMContentLoaded', () => {
  CartDrawer.init();
  AddToCart.init();
  MobileMenu.init();
  VariantSelector.init();
  ProductGallery.init();
  QtyControls.init();
  FilterButtons.init();
  StickyHeader.init();
  Newsletter.init();
  QuoteForm.init();
  CartPage.init();
  ScrollReveal.init();
  Search.init();
  AnnouncementBar.init();
});
